import { Badge, Dropdown, Icon, Menu } from "antd";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";

import { hasFeature } from "../../../Actions/UserActions";
import ActiveUsers from "../../../Components/Common/Menus/ActiveUsers";
import UserDropdown from "../../../Components/Common/User Dropdown/UserDropdown";
import { ScoutFeature } from "../../../Enums/ScoutFeature";
import { IUser } from "../../../Models/IUser";
import { showActiveUsersSelector } from "../../../Reducers/Selectors/UserSelectors";
import { setCurrentApp } from "../Actions/AppActions";
import { getRedirectLinkInitiate } from "../Actions/CommonActions";
import { getLoginUserData } from "../Actions/LoginActions";
import { getNotificationsInitiate, setNotificationsDrawerVisible } from "../Actions/NotificationActions";
import { setShowActiveUsers } from "../Actions/UserActions";
import { topNavHeightPx } from "../Constants/StyleConstants";
import { App } from "../Enums/App";
import { isAppOutsideOfScout } from "../Logic/AppLogic";
import { linkColor, treatLinkAsInternal } from "../Logic/NavLogic";
import { ILinkObject } from "../Models/ILinkObject";
import { ILoginUserData } from "../Models/Models";
import { linkListConfig } from "../Pages/Home/Login/LinkConfig";
import { currentAppSelector } from "../Selectors/AppSelectors";
import { loginSelector } from "../Selectors/LoginSelectors";
import {
    notificationsCountSelector,
    notificationsDrawerVisibleSelector,
    notificationsFilterDatesSelector
} from "../Selectors/NotificationsSelectors";

interface INavBarProps {
    isScout?: boolean;
    user: IUser;
}

const badgeOffset = {
    left: "-7px",
    top: "7px"
};

export const NavBar = (props: INavBarProps): JSX.Element => {
    /** misc */
    const dispatch = useDispatch();
    const history = useHistory();
    /** store */
    const currentApp: App = useSelector(currentAppSelector);
    const loginUser: ILoginUserData = useSelector(loginSelector);
    const showActiveUsers: boolean = useSelector(showActiveUsersSelector);
    const notificationCount: number = useSelector(notificationsCountSelector);
    const notificationsDrawerVisible: boolean = useSelector(notificationsDrawerVisibleSelector);
    const notificationFilterDates: [Date, Date] = useSelector(notificationsFilterDatesSelector);
    /** state */
    const [navMenuOpen, setNavMenuOpen] = useState<boolean>(false);
    const [iQUrl, setIqUrl] = useState("");
    const [factorUrl, setFactorUrl] = useState("");
    const [analyzeUrl, setAnalyzeUrl] = useState("");
    const [personaUrl, setPersonaUrl] = useState("");

    // Fix current app in old app world
    const href = history.location.pathname;
    useEffect(() => {
        if (href.includes("/Mobilytics") && currentApp !== App.Mobilytics) {
            dispatch(setCurrentApp(App.Mobilytics));
        } else if (href.includes("/Match") && currentApp !== App.Match) {
            dispatch(setCurrentApp(App.Match));
        } else if (href.includes("/MyReports") && currentApp !== App.MyReports) {
            dispatch(setCurrentApp(App.MyReports));
        } else if (href.includes("/Reports") && currentApp !== App.Reports) {
            dispatch(setCurrentApp(App.Reports));
        } else if (href.includes("/Settings") && currentApp !== App.UserSettings) {
            dispatch(setCurrentApp(App.UserSettings));
        } else if (href.includes("/Home/Scout") && currentApp !== App.Scout) {
            dispatch(setCurrentApp(App.Scout));
        }
    }, [currentApp, href]);
    /** effects */
    useEffect(() => {
        //This effect is here in the navbar because it's the 1 component that is common to both sections of the app (classic SCOUT and new UI)
        if (loginUser?.firstName?.trim() === "") {
            dispatch(getLoginUserData());
        }

        /** Get notifications for a user upon entering the screen */
        dispatch(getNotificationsInitiate(notificationFilterDates));
        /** poll for notifications - they may have a report that is being submitted */
        const notificationsTimer: NodeJS.Timeout = setInterval(() => {
            dispatch(getNotificationsInitiate(notificationFilterDates));
        }, 15000);//in milliseconds
        /** when the component un-mounts, stop the interval */
        return () => {
            clearInterval(notificationsTimer);
        };

    }, []);

    /** calling these endpoints ourselves to retrieve the link that redirects the user
     * to the appropriate external product
     */
    useEffect(() => {
        setIqUrl('/Redirect/OtherProducts?product=IQ');
        setFactorUrl('/Redirect/OtherProducts?product=Factor');
        setAnalyzeUrl('/Redirect/OtherProducts?product=Analyze');
        setPersonaUrl('/Redirect/OtherProducts?product=Persona');
    }, []);

    const navMenuClick = (): void => {
        setNavMenuOpen(!navMenuOpen);
    };

    const getCurrentAppFriendly = (): string => {
        switch (currentApp) {
            case App.Match: {
                return "match";
            }
            case App.Mobilytics: {
                return "mobilytics";
            }
            case App.MyReports:
            case App.Reports: {
                return "my reports";
            }
            case App.Scout: {
                return "scout";
            }
            case App.UserSettings: {
                return "user settings";
            }
            default: {
                return "";
            }
        }
    };

    const onBellClick = (): void => {
        dispatch(setNotificationsDrawerVisible(!notificationsDrawerVisible));
    };

    const onActiveUsersClose = (): void => {
        dispatch(setShowActiveUsers(false));
    };

    const onNavMenuItemClick = (app: App) => {
        if (app === App.Factor || app === App.Iq || app === App.Analyze || app === App.Persona) {
            if (app === App.Factor && loginUser?.featureAccess.indexOf(ScoutFeature.Factor) > -1) {
                dispatch(getRedirectLinkInitiate("Factor"));
            } else if (app === App.Iq && loginUser?.featureAccess.indexOf(ScoutFeature.IQ) > -1) {
                dispatch(getRedirectLinkInitiate("IQ"));
            } else if (app === App.Analyze && loginUser?.featureAccess.indexOf(ScoutFeature.Analyze) > -1) {
                dispatch(getRedirectLinkInitiate("Analyze"));
            } else if (app === App.Persona && loginUser?.featureAccess.indexOf(ScoutFeature.Persona) > -1) {
                dispatch(getRedirectLinkInitiate("Persona"));
            } else {
                window.location.href = `${window.location.origin}/Products#/`;
            }
        } else {
            dispatch(setCurrentApp(app));
        }
    };

    // Get external link for navLinks
    const getExternalLink = (isOutSideOfScout: boolean, link: ILinkObject, color: string) => {
        if (isOutSideOfScout) {
            let url = "";
            switch(link.app) {
                case App.Iq:
                    url = iQUrl;
                    break;
                case App.Factor:
                    url = factorUrl;
                    break;
                case App.Analyze:
                    url = analyzeUrl;
                    break;
                case App.Persona:
                    url = personaUrl;
                    break;
            }

            return (
                <a
                    href={url}
                    style={{
                        color: color
                    }}
                >
                    {link.title}
                </a>
            );
        } else {
            return (
                <a
                    href={link.externalLink}
                    style={{
                        color: color
                    }}
                >
                    {link.title}
                </a>
            );

        }
    };

    const navLinks = loginUser?.featureAccess
        ? linkListConfig(loginUser.featureAccess)
            .filter(link => link.exists)
            .map((link: ILinkObject) => {
                const color: string = linkColor(link, getCurrentAppFriendly());
                const isInternal: boolean = treatLinkAsInternal(link, currentApp);
                const outsideOfScout: boolean = isAppOutsideOfScout(link.app);

                return (
                    <Menu.Item
                        key={link.title}
                        {...(isInternal ? { onClick: () => onNavMenuItemClick(link.app) } : {})}
                    >
                        {
                            isInternal
                                ? (
                                    <Link
                                        to={link.link}
                                        style={{
                                            color: color,
                                        }}
                                    >
                                        {link.title}
                                    </Link>
                                )
                                : getExternalLink(outsideOfScout, link, color)
                        }
                    </Menu.Item>
                );
            })
        : [];


    const showNotifications = (): boolean => {
        if (props.user !== null) {
            return hasFeature(props.user, ScoutFeature.GpsInsights)
                || hasFeature(props.user, ScoutFeature.RetailMatchReports);
        }
        return false;
    };

    return (
        <div
            id="new-ui-navbar"
            style={{
                position: "fixed",
                top: 0,
                height: topNavHeightPx,
                width: "100%",
                display: "flex",
                alignItems: "center",
                padding: "11px 17px",
                backgroundColor: "#2e3d49",
                zIndex: 10
            }}
        >
            <Dropdown
                trigger={["click"]}
                overlay={
                    <Menu
                        style={{
                            width: 220,
                            boxShadow: "4px 6px 8px 0 rgba(0, 0, 0, 0.5)",
                        }}
                    >
                        {navLinks}
                    </Menu>
                }
            >
                <Icon
                    type="menu"
                    onClick={navMenuClick}
                    style={{
                        marginRight: "6px",
                        color: "#fff",
                        fontSize: "20px",
                    }}
                />
            </Dropdown>
            {
                props.isScout
                    ? <img
                        src="/Images/NEW_UI/rebrand/svg/SCOUT-White.svg"
                        style={{
                            height: "50px",
                            marginLeft: "15px"
                        }}
                    />
                    : <div
                        style={{
                            color: "#27E086",
                            fontSize: "36px",
                            fontWeight: "bold",
                        }}
                    >
                        {getCurrentAppFriendly()}
                    </div>
            }
            {showNotifications() && (
                <div
                    style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer"
                    }}
                    onClick={onBellClick}
                >
                    <Badge
                        count={notificationCount}
                        style={{
                            backgroundColor: "#E4AA4F",
                            color: "#fff"
                        }}
                        offset={[badgeOffset.left, badgeOffset.top]}>
                        <Icon
                            type="bell"
                            style={{
                                color: "#fff",
                                fontSize: "32px",
                                marginLeft: "auto",
                            }} />
                    </Badge>
                </div>
            )}
            {props.user !== null && (
                <div style={{
                    marginLeft: showNotifications()
                        ? "39px"
                        : "auto"
                }}>
                    <UserDropdown
                        isScout={props.isScout}
                        user={props.user}
                        showActiveUsers={showActiveUsers}
                        fromNewUI={true}
                    />
                </div>
            )}
            {showActiveUsers && (
                <ActiveUsers
                    onClose={onActiveUsersClose}
                />
            )}
        </div>
    );
};
